import React, { useEffect, useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import { KeyRound } from 'lucide-react';
import SettingsCard from './SettingsCard';
import axios from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';

export const membershipError = error => error?.response?.data?.code === 'RECENT_LOGIN_REQUIRED'
    ? 'Logga ut och logga in igen för att bekräfta din identitet. Öppna sedan ägarbytet eller åtkomstvalet och försök igen.'
    : error?.response?.data?.message || 'Ändringen kunde inte genomföras. Uppdatera sidan och försök igen.';

export default function OwnershipSection({ companyId, userId, onChanged }) {
    const [state, setState] = useState(null);
    const [members, setMembers] = useState([]);
    const [target, setTarget] = useState('');
    const [confirmed, setConfirmed] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [reload, setReload] = useState(0);
    useEffect(() => {
        if (!companyId || !userId) return;
        let cancelled = false;
        setState(null); setError(''); setMembers([]); setConfirmed(false);
        (async () => {
            try {
                const response = await axios.get(getApiUrl(`${companyId}/ownership`));
                if (cancelled) return;
                setState(response.data);
                if (response.data.ownerId === userId) {
                    const users = await axios.get(getApiUrl(`${companyId}/users`));
                    if (!cancelled) setMembers((users.data.users || []).filter(user => user.user_id !== userId));
                }
            } catch (failure) { if (!cancelled) setError(membershipError(failure)); }
        })();
        return () => { cancelled = true; };
    }, [companyId, userId, reload]);
    const act = async action => {
        setBusy(true); setError(''); setMessage('');
        try {
            if (action === 'request') await axios.post(getApiUrl(`${companyId}/ownership/transfers`), { targetUserId: target });
            else await axios.post(getApiUrl(`${companyId}/ownership/transfers/${state.transfer.transferId}/${action}`), {});
            setMessage(action === 'request' ? 'Ägarbytet väntar på mottagarens godkännande. Personen hittar det under Användarinställningar → Företag.'
                : action === 'accept' ? 'Ägarbytet är genomfört. Uppdatera sidan för att läsa in dina nya behörigheter.' : 'Ägarbytet är avslutat.');
            setReload(value => value + 1);
            onChanged?.();
        } catch (failure) { setError(membershipError(failure)); }
        finally { setBusy(false); }
    };
    if (!companyId || !userId) return null;
    const owner = state?.ownerId === userId;
    const pending = state?.transfer;
    return <SettingsCard id="agarskap" icon={KeyRound} title="Ägarskap för det valda företaget">
        {error && <Alert variant="danger" role="alert">{error}</Alert>}
        {message && <Alert variant="success" role="status">{message}</Alert>}
        <p>Företaget har en ägare. Ett ägarbyte gäller först när den nya ägaren godkänner det och erbjudandet gäller i ett dygn.</p>
        {(owner || pending) && <p>Den tidigare ägaren blir administratör och behåller företagstillgången även om en anställning avslutas. Den nya ägaren kan senare ändra den tillgången.</p>}
        {!state && !error && <p>Läser in ägarskap...</p>}
        {owner && !pending && <Form.Group className="mb-3" controlId="ownership-recipient">
            <Form.Label>Ny ägare</Form.Label>
            <Form.Control as="select" value={target} onChange={event => { setTarget(event.target.value); setConfirmed(false); }} disabled={busy}>
                <option value="">Välj en befintlig medlem</option>
                {members.map(user => <option key={user.user_id} value={user.user_id}>{[user.first_name, user.last_name].filter(Boolean).join(' ') || user.email}</option>)}
            </Form.Control>
        </Form.Group>}
        {pending && <p>{pending.toUserId === userId ? 'Du har blivit erbjuden att bli företagets ägare.' : 'Väntar på mottagarens godkännande.'}</p>}
        {((owner && !pending) || pending?.toUserId === userId) && <Form.Check className="mb-3" id="ownership-confirm" type="checkbox"
            label="Jag förstår ägarbytet och att den tidigare ägaren behåller administratörstillgång."
            checked={confirmed} onChange={event => setConfirmed(event.target.checked)} disabled={busy} />}
        <div className="d-flex flex-wrap gap-2">
            {owner && !pending && <Button disabled={busy || !target || !confirmed} onClick={() => act('request')}>Föreslå ägarbyte</Button>}
            {pending?.toUserId === userId && <>
                <Button disabled={busy || !confirmed} onClick={() => act('accept')}>Godkänn ägarbyte</Button>
                <Button variant="outline-secondary" disabled={busy} onClick={() => act('decline')}>Avböj</Button>
            </>}
            {pending?.fromUserId === userId && <Button variant="outline-secondary" disabled={busy} onClick={() => act('cancel')}>Avbryt ägarbyte</Button>}
            <Button variant="outline-secondary" disabled={busy} onClick={() => setReload(value => value + 1)}>Uppdatera ägarskap</Button>
        </div>
        {state && !owner && !pending && <p className="mt-3 mb-0">Inget ägarbyte väntar på ditt godkännande.</p>}
    </SettingsCard>;
}
